import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Calculator, Clock, MapPin, ArrowRight, Loader2, History, Truck } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";

const TATCalculator = ({ user, onLogout, Layout, isAdmin = false }) => {
  const [originPin, setOriginPin] = useState("");
  const [destinationPin, setDestinationPin] = useState("");
  const [shippingPartners, setShippingPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState("");
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // (Freight Estimator is a separate page)

  useEffect(() => {
    if (isAdmin) fetchPartners();
    fetchHistory();
  }, []);

  const fetchPartners = async () => {
    try {
      const res = await axios.get(`${API}/b2b-shipping-partner/`);
      const integrated = res.data.filter(p => p.api_base_url && p.is_active);
      setShippingPartners(integrated);
      if (integrated.length > 0) setSelectedPartner(integrated[0].id);
    } catch (e) {
      console.error("Failed to fetch partners", e);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/tat/history?limit=20`);
      setHistory(res.data);
    } catch (e) {
      console.error("Failed to fetch history", e);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCalculate = async () => {
    if (!originPin || originPin.length !== 6) {
      toast.error("Enter a valid 6-digit origin pincode");
      return;
    }
    if (!destinationPin || destinationPin.length !== 6) {
      toast.error("Enter a valid 6-digit destination pincode");
      return;
    }

    setCalculating(true);
    setResult(null);
    try {
      const body = {
        origin_pin: originPin,
        destination_pin: destinationPin,
      };
      if (isAdmin && selectedPartner) body.shipping_partner_id = selectedPartner;

      const res = await axios.post(`${API}/tat/estimate`, body);
      setResult(res.data);
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "TAT estimation failed");
    } finally {
      setCalculating(false);
    }
  };

  const loadFromHistory = (item) => {
    setOriginPin(item.origin_pin);
    setDestinationPin(item.destination_pin);
    setResult({
      origin_pin: item.origin_pin,
      destination_pin: item.destination_pin,
      shipping_partner: item.shipping_partner_name,
      tat_days: item.tat_days
    });
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">TAT Calculator</h1>
          <p className="text-slate-600">Estimate delivery transit time between pincodes</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calculator */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5" style={{ color: '#00A699' }} />
                  Estimate Delivery Time
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="origin">Origin Pincode</Label>
                    <Input
                      id="origin"
                      type="text"
                      maxLength={6}
                      placeholder="e.g. 400607"
                      value={originPin}
                      onChange={(e) => setOriginPin(e.target.value.replace(/\D/g, ""))}
                      data-testid="tat-origin-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="destination">Destination Pincode</Label>
                    <Input
                      id="destination"
                      type="text"
                      maxLength={6}
                      placeholder="e.g. 400604"
                      value={destinationPin}
                      onChange={(e) => setDestinationPin(e.target.value.replace(/\D/g, ""))}
                      data-testid="tat-destination-input"
                    />
                  </div>
                </div>

                {isAdmin && shippingPartners.length > 0 && (
                  <div>
                    <Label>Shipping Partner</Label>
                    <Select value={selectedPartner} onValueChange={setSelectedPartner}>
                      <SelectTrigger data-testid="tat-partner-select">
                        <SelectValue placeholder="Select Partner" />
                      </SelectTrigger>
                      <SelectContent>
                        {shippingPartners.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button
                  onClick={handleCalculate}
                  disabled={calculating || originPin.length !== 6 || destinationPin.length !== 6}
                  className="w-full"
                  style={{ backgroundColor: '#00A699' }}
                  data-testid="tat-calculate-btn"
                >
                  {calculating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <Calculator className="w-4 h-4 mr-2" />
                      Calculate TAT
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Result */}
            {result && (
              <Card className="border-0 shadow-lg border-l-4" style={{ borderLeftColor: '#00A699' }} data-testid="tat-result">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-sm text-slate-600">
                        <div className="flex items-center gap-1.5">
                          <MapPin className="w-4 h-4 text-green-600" />
                          <span className="font-medium">{result.origin_pin}</span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                        <div className="flex items-center gap-1.5">
                          <MapPin className="w-4 h-4 text-red-500" />
                          <span className="font-medium">{result.destination_pin}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-500">{result.shipping_partner}</span>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-5xl font-bold" style={{ color: '#00A699' }}>{result.tat_days}</div>
                      <div className="text-sm text-slate-500 mt-1">{result.tat_days === 1 ? 'Day' : 'Days'}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* History */}
          <div className="lg:col-span-1">
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <History className="w-4 h-4 text-slate-500" />
                  Search History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingHistory ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                ) : history.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">No searches yet</p>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {history.map((item, i) => (
                      <button
                        key={item.id || i}
                        onClick={() => loadFromHistory(item)}
                        className="w-full text-left p-4 rounded-lg border border-slate-100 hover:bg-teal-50 hover:border-teal-200 transition-colors"
                        data-testid={`tat-history-${i}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5 text-sm">
                            <span className="font-medium text-slate-700">{item.origin_pin}</span>
                            <ArrowRight className="w-3 h-3 text-slate-400" />
                            <span className="font-medium text-slate-700">{item.destination_pin}</span>
                          </div>
                          <Badge className="bg-teal-100 text-teal-700 border-0 text-xs">
                            {item.tat_days} {item.tat_days === 1 ? 'day' : 'days'}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-xs text-slate-400">
                          <span>{item.user_name || item.user_email || item.shipping_partner_name}</span>
                          <span>{formatToIST(item.created_at)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TATCalculator;

